import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PencilIcon, StarIcon, TimerIcon } from 'lucide-react';
import { ETICHETTA_FAMIGLIA_ATTRIBUTO } from '@calcio/shared';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ComandoIcona } from '../comuni/Azioni.js';
import { dataBreve } from '../comuni/dati.js';
import { Maglia } from '../comuni/Maglia.js';
import { IconaFamiglia, IconaOverall } from '../comuni/famiglie.js';
import { COLORE_STATO, coloreValore, coloreVoto } from './colori-stato.js';
import { type RigaRosaOrdinabile, FAMIGLIE_MEDIA, annoDi, etichettaRuolo } from './elenchi.js';
import { BadgeStati } from './Stati.js';

/**
 * Un tesserato: la maglia con il numero, con la fascia del colore dello stato
 * di salute e la sigla del ruolo, poi nome e stati, anno e piede (per gli
 * usciti il ruolo e il periodo) e, su una riga a se, minuti, voto e medie di
 * famiglia; a destra
 * l'overall. La matita del tesseramento, o il comando della vista che la
 * usa (lo spostamento di gruppo nella composizione della seduta, convocato e
 * titolare nella formazione), sta fuori dal collegamento.
 */
export function RigaGiocatore({
  riga: r,
  chiusi,
  apribile,
  conVoti,
  conMedie,
  onModifica,
  accanto,
  azione,
}: {
  riga: RigaRosaOrdinabile;
  chiusi: boolean;
  apribile: boolean;
  conVoti: boolean;
  conMedie: boolean;
  onModifica?: () => void;
  /** Dopo gli stati, accanto al nome: gli avvisi della vista che usa la riga. */
  accanto?: ReactNode;
  /** A destra, fuori dal collegamento: il comando della vista che usa la riga. */
  azione?: ReactNode;
}) {
  const corpo: ReactNode = (
    <>
      <Maglia
        numero={r.numeroMaglia}
        ruolo={r.ruoloPreferito}
        fascia={chiusi ? undefined : COLORE_STATO[r.stato]}
        className={cn(chiusi && 'opacity-60')}
      />

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-base font-semibold break-words">
            {r.cognome} {r.nome}
          </span>
          {!chiusi && <BadgeStati stato={r.stato} statiSportivi={r.statiSportivi} />}
          {accanto}
        </span>
        <span className="truncate text-sm text-muted-foreground tabular-nums">
          {chiusi
            ? `${etichettaRuolo(r.ruoloPreferito)} · ${dataBreve(r.dataInizio)} – ${dataBreve(r.dataFine!)}`
            : [annoDi(r.dataNascita), r.piede].filter(Boolean).join(' · ') || '—'}
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1" title="Minuti giocati">
            <TimerIcon aria-label="Minuti" className="size-4" />
            <span className="font-medium text-foreground tabular-nums">{r.minuti}</span>
          </span>
          {conVoti && (
            <span className="flex items-center gap-1" title="Voto medio">
              <StarIcon aria-label="Voto medio" className="size-4" />
              {r.votoMedio != null ? (
                <Badge className={cn('px-1.5 tabular-nums', coloreVoto(r.votoMedio))}>
                  {r.votoMedio.toLocaleString('it-IT')}
                </Badge>
              ) : (
                <span className="text-foreground">—</span>
              )}
            </span>
          )}
          {conMedie &&
            FAMIGLIE_MEDIA.map((f) => (
              <span
                key={f}
                className="flex items-center gap-1"
                title={`Media attributi ${ETICHETTA_FAMIGLIA_ATTRIBUTO[f].toLowerCase()}`}
              >
                <IconaFamiglia famiglia={f} className="size-4" />
                <span className="font-medium text-foreground tabular-nums">{r[f] ?? '—'}</span>
              </span>
            ))}
        </span>
      </span>

      {conMedie && <TasselloOverall overall={r.overall} />}
    </>
  );

  return (
    // Alta quanto chi la contiene: in una griglia a due colonne le righe affiancate si pareggiano.
    <div className="flex h-full items-center gap-2 rounded-lg border border-border bg-card p-2.5">
      {apribile ? (
        <Link
          to={`/giocatori/${r.giocatoreId}`}
          className="flex min-w-0 flex-1 items-center gap-3 self-stretch"
        >
          {corpo}
        </Link>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-3 self-stretch">{corpo}</span>
      )}
      {onModifica && (
        <ComandoIcona
          etichetta="Modifica il tesseramento"
          Icona={PencilIcon}
          size="icon-sm"
          onClick={onModifica}
        />
      )}
      {azione}
    </div>
  );
}

/** L'overall a destra della riga, con la fascia del colore del valore. */
export function TasselloOverall({ overall }: { overall: number | null }) {
  return (
    <span
      className="flex w-12 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-background text-center"
      title="Overall, media delle medie di famiglia"
    >
      <span
        className={cn(
          'flex justify-center py-0.5',
          overall != null ? coloreValore(overall) : 'bg-muted text-muted-foreground',
        )}
      >
        <IconaOverall aria-label="Overall" className="size-3.5" />
      </span>
      <span className="py-1 text-lg leading-6 font-bold tabular-nums">{overall ?? '—'}</span>
    </span>
  );
}
